import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { adminApi } from '@/api/adminApi';
import { ApiError } from '@/api/client';

export function AdminDashboardPage() {
  const [users, setUsers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [revenue, setRevenue] = useState<any>(null);
  const [tab, setTab] = useState<'overview' | 'users' | 'projects' | 'config' | 'transactions'>('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [u, p, r] = await Promise.all([
        adminApi.listUsers(),
        adminApi.listAllProjects(),
        adminApi.getRevenue(),
      ]);
      setUsers(u.items);
      setProjects(p.items);
      setRevenue(r);
    } catch (err) {
      if (err instanceof ApiError) console.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRoleChange(id: string, role: string) {
    try {
      await adminApi.updateUserRole(id, role);
      loadAll();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
  }

  async function handleDeleteUser(id: string) {
    if (!confirm('Xóa (vô hiệu hóa) user này?')) return;
    try {
      await adminApi.deleteUser(id);
      loadAll();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
  }

  return (
    <div className="h-full flex flex-col">
      <Header />
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="max-w-5xl mx-auto p-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Quản trị hệ thống</h1>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-4 overflow-x-auto">
            <TabButton active={tab === 'overview'} onClick={() => setTab('overview')}>Tổng quan</TabButton>
            <TabButton active={tab === 'users'} onClick={() => setTab('users')}>Người dùng</TabButton>
            <TabButton active={tab === 'projects'} onClick={() => setTab('projects')}>Đồ án</TabButton>
            <TabButton active={tab === 'config'} onClick={() => setTab('config')}>Cấu hình</TabButton>
            <TabButton active={tab === 'transactions'} onClick={() => setTab('transactions')}>Giao dịch</TabButton>
          </div>

          {loading && <p className="text-gray-500">Đang tải...</p>}

          {!loading && tab === 'overview' && revenue && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard label="Doanh thu Guest" value={`${(revenue.guestRevenue / 1000).toFixed(0)}k đ`} sub={`${revenue.guestCount} giao dịch`} />
              <StatCard label="Doanh thu Sub (30d)" value={`${(revenue.subscriptionRevenue30d / 1000).toFixed(0)}k đ`} sub={`${revenue.subCount} gói`} />
              <StatCard label="Tổng users" value={String(users.length)} sub="Đang hoạt động" />
              <StatCard label="Tổng đồ án" value={String(projects.length)} sub="Public + private" />
            </div>
          )}

          {!loading && tab === 'users' && (
            <div className="bg-white rounded-lg overflow-hidden border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-left">
                  <tr>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Tên</th>
                    <th className="px-3 py-2">Role</th>
                    <th className="px-3 py-2">Tạo</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                      <td className="px-3 py-2">{u.name}</td>
                      <td className="px-3 py-2">
                        <select
                          value={u.role}
                          onChange={(e) => handleRoleChange(u.id, e.target.value)}
                          className="border border-gray-300 rounded px-1 py-0.5 text-xs"
                        >
                          <option value="viewer">Viewer</option>
                          <option value="editor">Editor</option>
                          <option value="project_admin">Project Admin</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {new Date(u.created_at).toLocaleDateString('vi-VN')}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => handleDeleteUser(u.id)}
                          className="text-red-600 text-xs hover:underline"
                        >
                          Xóa
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === 'projects' && (
            <div className="bg-white rounded-lg overflow-hidden border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-left">
                  <tr>
                    <th className="px-3 py-2">Tên</th>
                    <th className="px-3 py-2">Owner</th>
                    <th className="px-3 py-2">Layers</th>
                    <th className="px-3 py-2">Trạng thái</th>
                    <th className="px-3 py-2">Tạo</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr key={p.id} className="border-t border-gray-100">
                      <td className="px-3 py-2">
                        <Link to={`/admin/projects/${p.id}`} className="text-brand-600 hover:underline">
                          {p.name}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-xs">{p.owner_name}</td>
                      <td className="px-3 py-2 text-center">{p.layer_count}</td>
                      <td className="px-3 py-2 text-xs">
                        {p.is_public ? '🌐 Public' : '🔒 Private'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {new Date(p.created_at).toLocaleDateString('vi-VN')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === 'config' && <ConfigPanel />}
          {!loading && tab === 'transactions' && <TransactionsPanel />}
        </div>
      </div>
    </div>
  );
}

function TabButton({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm whitespace-nowrap ${
        active ? 'text-brand-600 border-b-2 border-brand-600' : 'text-gray-600 hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function ConfigPanel() {
  const [items, setItems] = useState<any[]>([]);
  const [editing, setEditing] = useState<{ key: string; value: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await adminApi.getConfig();
      setItems(data.items);
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    if (!editing) return;
    try {
      await adminApi.setConfig(editing.key, editing.value);
      setEditing(null);
      load();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
  }

  if (loading) return <p className="text-gray-500">Đang tải...</p>;
  return (
    <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
      {items.map((item) => (
        <div key={item.key} className="px-4 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-mono text-gray-900">{item.key}</p>
            <p className="text-xs text-gray-500 truncate">{item.value}</p>
          </div>
          <button
            onClick={() => setEditing({ key: item.key, value: item.value })}
            className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
          >
            Sửa
          </button>
        </div>
      ))}

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="font-bold mb-3">Sửa: {editing.key}</h3>
            <input
              type="text"
              value={editing.value}
              onChange={(e) => setEditing({ ...editing, value: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded"
            />
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm">Hủy</button>
              <button onClick={save} className="px-4 py-2 text-sm bg-brand-600 text-white rounded">Lưu</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TransactionsPanel() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.listTransactions().then((d) => {
      setItems(d.items);
      setLoading(false);
    });
  }, []);

  if (loading) return <p className="text-gray-500">Đang tải...</p>;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600 text-left">
          <tr>
            <th className="px-3 py-2">Mã</th>
            <th className="px-3 py-2">User</th>
            <th className="px-3 py-2">Loại</th>
            <th className="px-3 py-2">Số tiền</th>
            <th className="px-3 py-2">Trạng thái</th>
            <th className="px-3 py-2">Ngày</th>
          </tr>
        </thead>
        <tbody>
          {items.map((t) => (
            <tr key={t.id} className="border-t border-gray-100">
              <td className="px-3 py-2 font-mono text-xs">{t.id.slice(0, 8)}</td>
              <td className="px-3 py-2 text-xs">{t.user_email ?? 'Guest'}</td>
              <td className="px-3 py-2 text-xs">
                {t.type === 'guest_query' ? '☕ Guest' : `📦 ${t.tier?.toUpperCase()}`}
              </td>
              <td className="px-3 py-2">{t.amount.toLocaleString('vi-VN')} đ</td>
              <td className="px-3 py-2">
                <span
                  className={`px-2 py-0.5 text-xs rounded ${
                    t.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                    t.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-600'
                  }`}
                >
                  {t.status}
                </span>
              </td>
              <td className="px-3 py-2 text-xs text-gray-500">
                {new Date(t.created_at).toLocaleString('vi-VN')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
